import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminFAQsPage = () => {
  const { getAuthHeader } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '', order: 0 });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ question: item.question, answer: item.answer, order: item.order });
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ question: '', answer: '', order: items.length });
  };

  const handleCancel = () => {
    setEditingId(null); setIsAdding(false);
    setFormData({ question: '', answer: '', order: 0 });
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        await axios.post(`${API}/faqs`, formData, { headers: getAuthHeader() });
        toast.success('FAQ added');
      } else {
        await axios.put(`${API}/faqs/${editingId}`, formData, { headers: getAuthHeader() });
        toast.success('FAQ updated');
      }
      fetchItems(); handleCancel();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/faqs/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard"><Button variant="outline" size="icon" className="rounded-full"><ArrowLeft size={18} /></Button></Link>
              <div><h1 className="font-heading text-3xl font-bold">Manage FAQs</h1></div>
            </div>
            {!isAdding && !editingId && <Button onClick={handleAdd} className="bg-[#10B981] rounded-full"><Plus size={18} className="mr-2" />Add FAQ</Button>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {(isAdding || editingId) && (
          <div className="bg-white rounded-2xl p-8 border mb-8">
            <h2 className="font-heading text-2xl font-bold mb-6">{isAdding ? 'Add' : 'Edit'} FAQ</h2>
            <div className="space-y-6">
              <div><Label>Question</Label><Input value={formData.question} onChange={(e) => setFormData({...formData, question: e.target.value})} /></div>
              <div><Label>Answer</Label><Textarea value={formData.answer} onChange={(e) => setFormData({...formData, answer: e.target.value})} rows={4} /></div>
              <div className="flex gap-4">
                <Button onClick={handleSave} className="bg-[#10B981] rounded-full"><Save size={18} className="mr-2" />Save</Button>
                <Button onClick={handleCancel} variant="outline" className="rounded-full"><X size={18} className="mr-2" />Cancel</Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border">
              <p className="text-slate-600 mb-4">No FAQs yet</p>
              <Button onClick={handleAdd} className="bg-[#10B981] rounded-full"><Plus size={18} className="mr-2" />Add FAQ</Button>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl p-6 border hover:border-[#10B981]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold mb-2">{item.question}</h3>
                    <p className="text-slate-600">{item.answer}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(item)} variant="outline" size="icon" className="rounded-full"><Edit size={18} /></Button>
                    <Button onClick={() => handleDelete(item.id)} variant="outline" size="icon" className="rounded-full text-red-600"><Trash2 size={18} /></Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
